import { useMemo } from 'react';

import DOMPurify from 'dompurify';

import './WorkItemHtml.css';

const ATTACHMENT_ID_RE = /\/_apis\/wit\/attachments\/([0-9a-fA-F-]{36})/;

/**
 * Render an Azure DevOps work-item HTML field (description, acceptance
 * criteria, repro steps, comment body) safely. Two responsibilities:
 *
 *   1. DOMPurify-strip any unsafe markup so we can dangerouslySetInnerHTML
 *      without hosting an XSS vector. Defaults are conservative — script,
 *      iframe, on* handlers, javascript: URLs all gone.
 *   2. Rewrite <img src> URLs that point at ADO attachment endpoints to go
 *      through our authenticated proxy at
 *      /api/azure-devops/work-items/:id/attachments/:attachmentId so the
 *      browser can actually load them (the original URL requires a bearer
 *      token on every request).
 */
export const WorkItemHtml = ({ className = '', html, style, workItemId }) => {

    const sanitized = useMemo(() => {

        if (!html || typeof html !== 'string') return '';

        // Pre-rewrite attachment image srcs so DOMPurify's URL filtering
        // doesn't strip them. We do this on a string before sanitization
        // so the rewrite is part of the cleaned-up output, not a
        // dangerouslySet override.
        const rewritten = html.replace(/<img\b([^>]*?)src\s*=\s*["']([^"']+)["']([^>]*)>/gi, (full, before, src, after) => {

            const m = src.match(ATTACHMENT_ID_RE);
            if (!m || !workItemId) return full;
            const proxied = `/api/azure-devops/work-items/${workItemId}/attachments/${m[1]}`;
            return `<img${before}src="${proxied}"${after}>`;

        });

        // Keep the tags ADO actually uses; drop scripty ones implicitly.
        return DOMPurify.sanitize(rewritten, {
            ALLOWED_ATTR: ['alt', 'class', 'href', 'rel', 'src', 'style', 'target', 'title'],
            ALLOWED_TAGS: ['a', 'b', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'i', 'img', 'li', 'ol', 'p', 'pre', 'span', 'strong', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'u', 'ul']
        });

    }, [html, workItemId]);

    if (!sanitized) return null;

    // Sanitized via DOMPurify above — this is the established pattern for
    // rendering trusted-but-html content.
    return (
        <div
            className={`ado-html ${className}`}
            dangerouslySetInnerHTML={{ __html: sanitized }}
            style={style}
        />
    );

};
